<!-- 講座画面、メンバー一覧選択時のページ -->
<script setup lang="ts">
  import { useDisplay } from "vuetify";
  import { tableHeaders, spRows } from "../Atoms/DataTable/PlanMemberListHeader";
  import MemberListTagFilterModal from "../Molecules/MemberListTagFilterModal.vue";
  import PlanReviewModal from "./PlanReview/Modal.vue";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { usePlanStore } from "~/stores/PlanStore";
  import { DEFAULT_PAGE_SIZE } from "~/consts/contentsConfig";
  import type { ContractMember } from "~/entities/ContractMember";
  import type { UserTagType } from "~/entities/UserTagType";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {});

  const planReviewModal = ref<InstanceType<typeof PlanReviewModal> | null>(null);

  defineEmits(["onBack"]);

  const page = ref(1);
  const pageSize = ref(30);
  const showDetail = ref(false);

  const defaultSort = "desc";
  const defaultSortItem = "contract_day";

  // データ取得量
  const dataVolume = computed<string>(() => {
    return (useRoute().query.dataVolume as string) || "allData";
  });
  const sort = computed<string>(() => String(useRoute().query.sort ?? defaultSort));
  const sortItem = computed<string>(() => String(useRoute().query.sort_item ?? defaultSortItem));
  const search = computed({
    get: () => (useRoute().query.search as string) || "",
    set: value => {
      const queries = filterEmptyObject({ ...useRoute().query, search: value });
      historyStore.to({ query: queries });
    },
  });

  const selectedFilters = computed<string[]>(() => {
    const filters = [];
    if (planStore.filterMemberConditions.userTagConditions.length > 0) {
      filters.push("price_tag");
      filters.push("plan_tag");
    }
    return filters;
  });

  const memberListTagFilterModal = ref<InstanceType<typeof MemberListTagFilterModal> | null>(null);

  // レスポンシブ対応
  const { name } = useDisplay();
  const isSp = computed<boolean>((): boolean => name.value === "xs");

  const planStore = usePlanStore();

  // 講座ID
  const planId = computed<string>(() => {
    return (useRoute().query.planId as string) || localProps.planInfo.id;
  });

  // トランジションエフェクトの関係でデータ取得は外部から行う
  const fetch = () => {
    const options = {
      plan_id: "",
      sort: sort.value,
      sort_item: sortItem.value,
      user_name: search.value,
      user_tag_conditions: planStore.filterMemberConditions.userTagConditions.map(item => ({
        tag_key1: item.userTagKey1,
        tag_key2: item.userTagKey2,
        tag_key3: item.userTagKey3,
        user_tag_type_id: item.userTagTypeId,
      })),
      display_tag: "owner_plan",
    };
    if (dataVolume.value !== "allData") {
      options.plan_id = planId.value || "";
      options.display_tag = "plan_price";
    }
    planStore.fetchMembers(page.value, pageSize.value, options);
    // ページが変わったらスクロール位置をトップに戻す
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const loading = computed<boolean>(() => planStore.loadingMembers);

  const computedPage = computed<number>({
    get: () => page.value,
    set: value => {
      page.value = value;
      fetch();
    },
  });

  const showPagination = computed<boolean>(() => {
    if (planStore.contractMemberResponse?.totalPages) {
      return planStore.contractMemberResponse.totalPages > 1;
    }
    return false;
  });

  watch(
    () => dataVolume.value,
    () => {
      planStore.setFilterMemberTagConditions([]);
    },
  );

  watch(
    () => [
      search.value,
      sort.value,
      sortItem.value,
      search.value,
      dataVolume.value,
      planStore.filterMemberConditions.userTagConditions,
    ],
    () => {
      page.value = 1;
      fetch();
    },
  );

  const openReviewModal = (planId: string, userId: string) => {
    planReviewModal.value?.openModal(planId, userId);
  };

  const getTableHeaders = () => {
    return tableHeaders(
      isSp.value,
      dataVolume.value as "allData" | "planBase",
      planStore.filterMemberConditions.userTagConditions,
      openReviewModal,
    );
  };

  const handkeCloseReviewModal = (modified: boolean) => {
    if (modified) {
      fetch();
    }
  };

  const handleClickTableHeader = (type: string, key: string) => {
    if (type === "filter" && key.includes("tag")) {
      // フィルター処理
      const options = {
        plan_id: "",
      };
      if (dataVolume.value !== "allData") {
        options.plan_id = planId.value || "";
      }
      memberListTagFilterModal.value?.openModal(planStore.filterMemberConditions.userTagConditions, options);
    }
  };

  const handleClickTableItem = (item: ContractMember) => {
    historyStore.to(`/plan/${planId.value}/member/${item.clientUser.userId}/detail`);
  };

  const filterTagType = computed<string[]>(() => {
    return dataVolume.value === "allData" ? ["plan"] : ["price"];
  });

  defineExpose({
    fetch,
    loading,
  });
</script>
<template>
  <MoleculesMemberListTagFilterModal
    ref="memberListTagFilterModal"
    :type="filterTagType"
    @on-confirm="(val: UserTagType[]) => planStore.setFilterMemberTagConditions(val)"
  />
  <MoleculesContainerMain
    v-model:search="search"
    :show-search="true"
    :show-plans="true"
    :default-plan-id="planInfo.id"
    title="会員一覧"
    svg-icon="assets/images/icon/member-list.svg"
    :loading="loading"
    @on-back="$emit('onBack')"
  >
    <div class="mx-2">
      <div class="d-flex mb-6 align-center" :class="{ 'justify-center': isSp, 'flex-column ga-2': isSp }">
        <div class="d-flex flex-wrap justify-center">
          <div class="ma-2">
            <div class="text-title">累計会員数</div>
            <div class="text-count">
              <v-progress-circular v-if="loading" :size="20" :width="2" indeterminate></v-progress-circular>
              <div v-else>
                {{ numberFormat(planStore.contractMemberResponse?.contractCount?.totalMemberCount ?? 0) }}
              </div>
            </div>
          </div>
          <div class="ma-2">
            <div class="text-title">累計解約会員数</div>
            <div class="text-count">
              <v-progress-circular v-if="loading" :size="20" :width="2" indeterminate></v-progress-circular>
              <div v-else>
                {{ numberFormat(planStore.contractMemberResponse?.contractCount?.totalCancellationMemberCount ?? 0) }}
              </div>
            </div>
          </div>
          <div class="ma-2">
            <div class="text-title">現在の会員数</div>
            <div class="text-count">
              <v-progress-circular v-if="loading" :size="20" :width="2" indeterminate></v-progress-circular>
              <div v-else>
                {{ numberFormat(planStore.contractMemberResponse?.contractCount?.activeMemberCount ?? 0) }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isSp" class="flex-grow-1"></div>
      </div>

      <div v-if="!loading && isSp" class="d-flex flex-wrap justify-end">
        <v-btn variant="tonal" @click="showDetail = !showDetail">
          <v-icon :icon="showDetail ? '$mdiChevronUp' : '$mdiChevronDown'" />
        </v-btn>
      </div>

      <template v-if="loading">
        <AtomsDataTablePlanMemberCardLoading v-for="index in DEFAULT_PAGE_SIZE" :key="index" />
      </template>
      <template v-else>
        <MoleculesDatatable
          :headers="getTableHeaders()"
          :items="planStore.contractMemberResponse?.contractMembers || []"
          :sp-rows="spRows()"
          :show-detail="showDetail"
          :sp-default-rows="2"
          :hoverable="true"
          row-cursor="pointer"
          :default-sort="defaultSort"
          :default-sort-item="defaultSortItem"
          :selected-filters="selectedFilters"
          @on-click-header="handleClickTableHeader"
          @on-click-item="handleClickTableItem"
        />
      </template>

      <v-sheet color="transparent" class="pb-4">
        <AtomsPagination
          v-if="showPagination"
          v-model:page="computedPage"
          :total-pages="planStore.contractMemberResponse?.totalPages || 0"
        />
      </v-sheet>
    </div>
  </MoleculesContainerMain>
  <OrganismsPlanReviewModal ref="planReviewModal" @on-close="handkeCloseReviewModal" />
</template>

<style lang="scss" scoped>
  .text-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.8em;
    margin-bottom: 4px;
    text-align: center;
  }

  .text-count {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.8em;
    text-align: center;
  }
</style>
